import * as R from 'ramda';

import generateId from '../../utils/generateId';

const sort = R.sort((a, b) => a - b);

const compareArrays = (arr1, arr2) => {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
    if (arr1.length !== arr2.length) return false;

    const a1 = sort(arr1);
    const a2 = sort(arr2);

    for (let i = 0; i < a1.length; i++) {
        if (a1[i] !== a2[i]) return false;
    }

    return true;
};

const compPathList = (paths, obj1, obj2) => {
    for (let path of paths) {
        const val1 = R.path(path, obj1);
        const val2 = R.path(path, obj2);

        const isSame = Array.isArray(val1)
            ? compareArrays(val1, val2)
            : val1 === val2;

        if (!isSame) return false;
    }
    return true;
};

const FILTER_PATHS = [
    ['startDate'],
    ['endDate'],
    ['dateSpec'],
    ['compareDates'],
    ['rightsholders', 'selected'],
    ['sponsors', 'selected'],
    ['placements', 'selected'],
    ['placementTypes', 'selected'],
    ['accOwnTypes', 'selected'],
    ['mediaTypes', 'selected'],
    ['platformPostTypePairs', 'selected'],
    ['audiencePlatforms', 'selected'],
    ['collections', 'selected'],
    ['rightsholdersLeaguePairs'],
    ['accountUsernames'],
    ['networks'],
    ['hashtags'],
    ['captions'],
    ['mvpScore'],
    ['broadcastNames'],
    ['broadcastOptions'],
    ['insightsGroupBy'],
    ['sort'],
    ['order'],
    ['countries', 'selected'],
    ['broadcastNetworks', 'selected'],
    ['authorshipTypes'],
    ['collaboratingAccountUsernames'],
    ['ageBrackets'],
    ['genders'],
];

const saveFilters = () => (state, newFilters) => {
    const changesId = generateId();
    const overridesId = generateId();

    const userClientId = state.user.selectedUserClientId;

    const evolvedState = R.evolve(
        {
            clients: {
                [userClientId]: {
                    filters: {
                        changesId: R.always(changesId),
                        overridesId: R.always(overridesId),
                        startDate: R.always(newFilters.startDate),
                        endDate: R.always(newFilters.endDate),
                        compareDates: R.always(newFilters.compareDates),
                        rightsholders: {
                            selected: R.always(newFilters.rightsholders),
                        },
                        rightsholdersLeaguePairs: R.always(
                            newFilters.rightsholdersLeaguePairs,
                        ),
                        sponsors: {
                            selected: R.always(newFilters.sponsors),
                        },
                        placements: {
                            selected: R.always(newFilters.placements),
                        },
                        placementTypes: {
                            selected: R.always(newFilters.placementTypes),
                        },
                        accOwnTypes: {
                            selected: R.always(newFilters.accOwnTypes),
                        },
                        mediaTypes: {
                            selected: R.always(newFilters.mediaTypes),
                        },
                        platformPostTypePairs: {
                            selected: R.always(
                                newFilters.platformPostTypePairs,
                            ),
                        },
                        audiencePlatforms: {
                            selected: R.always(newFilters.audiencePlatforms),
                        },
                        accountUsernames: R.always(newFilters.accountUsernames),
                        networks: R.always(newFilters.networks),
                        hashtags: R.always(newFilters.hashtags),
                        captions: R.always(newFilters.captions ?? []),
                        collections: {
                            selected: R.always(newFilters.collections),
                        },
                        mvpScore: R.always(newFilters.mvpScore),
                        authorshipTypes: R.always(
                            newFilters.authorshipTypes ?? [],
                        ),
                        collaboratingAccountUsernames: R.always(
                            newFilters.collaboratingAccountUsernames ?? [],
                        ),
                        broadcastNames: R.always(newFilters.broadcastNames),
                        broadcastOptions: R.always(newFilters.broadcastOptions),
                        insightsGroupBy: R.always(
                            newFilters?.insightsGroupBy || [],
                        ),
                        sort: R.always(newFilters.sort),
                        order: R.always(newFilters.order),
                        isReturningFromOverride: R.always(false),
                        countries: {
                            selected: R.always(newFilters.countries),
                        },
                        broadcastNetworks: {
                            selected: R.always(newFilters.broadcastNetworks),
                        },
                        dateRange: R.always(newFilters.dateRange),
                        userDefaultFilters: {
                            filterJson: {
                                dateRange: R.always(newFilters.dateRange),
                            },
                        },
                    },
                    media: {
                        filtersOverride: R.always(null),
                    },
                    broadcasts: {
                        filtersOverride: R.always(null),
                    },
                },
            },
        },
        state,
    );

    // `R.evolve` only writes transformations for keys the target already has,
    // and `clients` is rehydrated verbatim from localStorage — a session
    // opened before this release has no ageBrackets / genders key, so they
    // have to be set outright or the selection silently never persists. The same
    // goes for dateSpec on sessions that predate it.
    // Same trap as `rightsholderAttributionMode`; see clientInitialState.
    const newState = R.pipe(
        R.assocPath(
            ['clients', userClientId, 'filters', 'ageBrackets'],
            newFilters.ageBrackets ?? [],
        ),
        R.assocPath(
            ['clients', userClientId, 'filters', 'genders'],
            newFilters.genders ?? [],
        ),
        R.assocPath(
            ['clients', userClientId, 'filters', 'dateSpec'],
            newFilters.dateSpec ?? null,
        ),
    )(evolvedState);

    const didChange = !compPathList(
        FILTER_PATHS,
        state.clients[userClientId].filters,
        newState.clients[userClientId].filters,
    );

    const hasOverride =
        state.clients[userClientId].media.filtersOverride !== null;
    const shouldChangeState = !hasOverride && didChange;

    return shouldChangeState ? newState : state;
};

export default saveFilters;
